/* eslint-env browser */
// Payment settings (wave 3, W2_Portal_Settings look): one tab per payment
// gateway, each setting on its own row (switches for on / off values, a
// masked box with a show button for keys and secrets), and a save bar that
// counts unsaved changes across every gateway.
//
// Save writes exactly what the old Submit wrote: the WHOLE providers object
// through editPaymentMethods (payulatam merchantId / accountId stay whole
// numbers). Admin only (the route), and only with critical edits on
// (settings.AllowCriticalEditsAdmin), as before. Secrets are never printed
// or logged; they show as dots until someone presses the eye button.
// Differences from the old form: focusing a key box no longer empties it
// (a stray click used to wipe a saved secret from the draft), and the store
// is no longer changed in place while typing (the saved copy is kept here,
// so the tab reopens on what was saved).
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Switch from '@mui/material/Switch';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import { api } from 'common';
import { showToast, confirmDialog } from '../components/feedback';
import { tokens as c } from '../theme';
import { SectionCard, SettingRow, SaveBar, UnsavedDot } from '../components/modern';

// The saved copy survives switching away from this page for the rest of the
// session while the store still holds the providers it started from (the
// store keeps the list loaded at sign-in; a save does not update it).
let savedCache = null; // { source, data }

const clone = (obj) => JSON.parse(JSON.stringify(obj));
const same = (a, b) => JSON.stringify(a === undefined ? null : a) === JSON.stringify(b === undefined ? null : b);
const isBool = (v) => v === true || v === false;
const fieldId = (pg, key) => `pay-${pg}-${key}`.replace(/[^A-Za-z0-9_-]/g, '_');

// Wide screens (lg and up): a gateway's settings in two columns; one column
// below lg as before. The last grid row ends without a line (the save bar
// draws one); the very last row never has one.
const ROWS_SX = {
  display: 'grid',
  gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'repeat(2, minmax(0, 1fr))' },
  columnGap: 4,
  '& > :nth-last-of-type(2):nth-of-type(odd)': { borderBottom: { lg: 0 } },
};

const startFrom = (source) => {
  if (!source) return null;
  if (savedCache && savedCache.source === source) return clone(savedCache.data);
  return clone(source);
};

export default function PaymentSettings() {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const { editPaymentMethods } = api;
  const paymentmethods = useSelector((state) => state.paymentmethods);
  const settings = useSelector((state) => state.settingsdata.settings);
  const source = paymentmethods.providers;

  const [value, setValue] = useState(0);
  const [base, setBase] = useState(() => startFrom(source));
  const [draft, setDraft] = useState(() => startFrom(source));
  const [shown, setShown] = useState({}); // field id -> secret visible
  const [saving, setSaving] = useState(false);
  const sourceRef = useRef(source);
  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (source !== sourceRef.current) {
      sourceRef.current = source;
      setBase(startFrom(source));
      setDraft(startFrom(source));
    }
  }, [source]);

  const gateways = draft ? Object.keys(draft) : [];
  const safeTab = Math.min(value, Math.max(0, gateways.length - 1));

  const changed = useMemo(() => {
    if (!draft || !base) return [];
    const out = [];
    Object.keys(draft).forEach((pg) => {
      Object.keys(draft[pg] || {}).forEach((key) => {
        if (!same(draft[pg][key], base[pg] ? base[pg][key] : undefined)) out.push(`${pg}|${key}`);
      });
    });
    return out;
  }, [draft, base]);
  const changedGateways = useMemo(() => new Set(changed.map((k) => k.split('|')[0])), [changed]);

  const setField = (pg, key, v) => setDraft((d) => ({ ...d, [pg]: { ...d[pg], [key]: v } }));

  const handleText = (pg, key, raw) => {
    // As before: payulatam's merchantId and accountId are whole numbers.
    const v = pg === 'payulatam' && (key === 'merchantId' || key === 'accountId') && raw.length > 0 ? parseInt(raw) : raw;
    setField(pg, key, v);
  };

  const discard = async () => {
    if (!changed.length || saving) return;
    const ok = await confirmDialog({
      title:
        changed.length === 1
          ? t('settings_discard_title_one', { defaultValue: 'Discard 1 unsaved change?' })
          : t('settings_discard_title_many', { n: changed.length, defaultValue: 'Discard {{n}} unsaved changes?' }),
      body: t('settings_discard_body', { defaultValue: 'The settings go back to how they were when you last saved.' }),
      confirmLabel: t('settings_discard', { defaultValue: 'Discard' }),
      destructive: true,
    });
    if (ok && aliveRef.current) setDraft(clone(base));
  };

  const handleSubmit = () => {
    if (!draft || saving) return;
    if (!(settings && settings.AllowCriticalEditsAdmin)) {
      showToast.warning(t('demo_mode'));
      return;
    }
    setSaving(true);
    const payload = clone(draft);
    try {
      dispatch(editPaymentMethods(payload));
      savedCache = { source: sourceRef.current, data: clone(payload) };
      setBase(clone(payload));
      showToast.success(t('saved', { defaultValue: 'Saved' }));
    } finally {
      setSaving(false);
    }
  };

  // Nothing to edit until the gateways are loaded (as before).
  if (!draft) return null;

  const pg = gateways[safeTab];
  const entries = pg ? Object.keys(draft[pg] || {}) : [];

  return (
    <Box dir={i18n.dir()} sx={{ width: '100%' }}>
      <Tabs
        value={safeTab}
        onChange={(e, v) => setValue(v)}
        variant="scrollable"
        allowScrollButtonsMobile
        aria-label={t('payment_settings')}
        sx={{ borderBottom: `1px solid ${c.OUTLINE}` }}
      >
        {gateways.map((name, index) => (
          <Tab
            key={name}
            id={`pay-tab-${index}`}
            aria-controls={`pay-tabpanel-${index}`}
            label={
              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                {changedGateways.has(name) ? <UnsavedDot /> : null}
                {t(name)}
              </Box>
            }
          />
        ))}
      </Tabs>
      <Box role="tabpanel" id={`pay-tabpanel-${safeTab}`} aria-labelledby={`pay-tab-${safeTab}`} sx={{ pt: 2.5 }}>
        {pg ? (
          <SectionCard
            id={`pay-${pg}`}
            icon={<PaymentsOutlinedIcon />}
            hue="success"
            title={t(pg)}
            sx={{ display: 'flex', flexDirection: 'column', pb: 0 }}
          >
            <Box sx={{ mt: 0.5, ...ROWS_SX }}>
              {entries.map((key) => {
                const id = fieldId(pg, key);
                const v = draft[pg][key];
                const was = base && base[pg] ? base[pg][key] : undefined;
                if (isBool(v)) {
                  return (
                    <SettingRow
                      key={key}
                      id={id}
                      layout="switch"
                      label={key}
                      changed={!same(v, was)}
                      control={<Switch id={id} checked={v} onChange={(e) => setField(pg, key, e.target.checked)} sx={{ my: -0.75 }} />}
                    />
                  );
                }
                const visible = !!shown[id];
                const label = visible ? t('hide', { defaultValue: 'Hide' }) : t('show', { defaultValue: 'Show' });
                return (
                  <SettingRow
                    key={key}
                    id={id}
                    label={key}
                    changed={!same(v, was)}
                    control={
                      <TextField
                        id={id}
                        size="small"
                        type={visible ? 'text' : 'password'}
                        value={v === undefined || v === null ? '' : v}
                        onChange={(e) => handleText(pg, key, e.target.value)}
                        autoComplete="new-password"
                        inputProps={{ spellCheck: false, 'data-lpignore': 'true' }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <Tooltip title={`${label}: ${key}`}>
                                <IconButton
                                  edge="end"
                                  size="small"
                                  aria-label={`${label}: ${key}`}
                                  aria-pressed={visible ? 'true' : 'false'}
                                  onClick={() => setShown((s) => ({ ...s, [id]: !s[id] }))}
                                >
                                  {visible ? <VisibilityOffOutlinedIcon fontSize="small" /> : <VisibilityOutlinedIcon fontSize="small" />}
                                </IconButton>
                              </Tooltip>
                            </InputAdornment>
                          ),
                        }}
                        sx={{ width: { xs: '100%', sm: 420 } }}
                      />
                    }
                  />
                );
              })}
            </Box>
            <SaveBar
              changedCount={changed.length}
              onDiscard={discard}
              onSave={handleSubmit}
              saving={saving}
              sx={{ mx: { xs: -2, md: -2.5 }, px: { xs: 2, md: 2.5 } }}
            />
          </SectionCard>
        ) : null}
      </Box>
    </Box>
  );
}
